import { notifyAuthExpired } from './authEvents';
import { refreshTokens } from './authApi';
import { clearTokens, getTokens, isAccessExpired, type AuthTokens } from './tokenStore';

export { AUTH_EXPIRED_EVENT } from './authEvents';
export type { AuthExpiredDetail } from './authEvents';

let refreshInFlight: Promise<AuthTokens> | null = null;

type EnsureFreshOptions = {
  forceRefresh?: boolean;
  silent?: boolean;
  redirectOnExpire?: boolean;
};

async function refreshAccessToken(refreshToken: string): Promise<AuthTokens> {
  if (!refreshInFlight) {
    refreshInFlight = refreshTokens(refreshToken).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function ensureFreshAccess(options: EnsureFreshOptions = {}): Promise<AuthTokens | null> {
  const tokens = getTokens();
  if (!tokens) {
    return null;
  }

  const shouldRefresh = options.forceRefresh || isAccessExpired(tokens);
  if (!shouldRefresh) {
    return tokens;
  }

  try {
    return await refreshAccessToken(tokens.refreshToken);
  } catch {
    clearTokens();
    if (!options.silent) {
      notifyAuthExpired({ redirect: !!options.redirectOnExpire });
    }
    return null;
  }
}

export async function bootstrapSession(): Promise<AuthTokens | null> {
  return ensureFreshAccess({ silent: true });
}

export type FetchWithAuthOptions = RequestInit & {
  skipAuth?: boolean;
  redirectOnUnauthorized?: boolean;
};

export async function fetchWithAuth(input: RequestInfo | URL, init: FetchWithAuthOptions = {}) {
  const { skipAuth, redirectOnUnauthorized, headers, ...rest } = init;
  const tokens = skipAuth ? null : await ensureFreshAccess();

  const doFetch = (accessToken?: string) =>
    fetch(input, {
      ...rest,
      headers: {
        ...(headers ?? {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });

  const resp = await doFetch(tokens?.accessToken);
  if (resp.status !== 401 || skipAuth) {
    return resp;
  }

  const latest = await ensureFreshAccess({
    forceRefresh: true,
    redirectOnExpire: redirectOnUnauthorized,
  });
  if (!latest) {
    if (redirectOnUnauthorized) {
      notifyAuthExpired({ redirect: true });
    }
    return resp;
  }

  const retryResp = await doFetch(latest.accessToken);
  if (retryResp.status === 401) {
    clearTokens();
    if (redirectOnUnauthorized) {
      notifyAuthExpired({ redirect: true });
    }
  }
  return retryResp;
}
